import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../../lib/supabase";
import { useNavigate } from "react-router-dom";
import InstitutionSelector from "../../components/InstitutionSelector";

const formatPhoneNumber = (value) => {
  if (!value) return value;
  const phoneNumber = value.replace(/[^\d]/g, "");
  const phoneNumberLength = phoneNumber.length;
  if (phoneNumberLength < 4) return phoneNumber;
  if (phoneNumberLength < 7) {
    return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
  }
  return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(
    3,
    6
  )}-${phoneNumber.slice(6, 10)}`;
};

const validatePhoneNumber = (phone) => {
  if (!phone) return true; // Optional field
  const digitsOnly = phone.replace(/[^\d]/g, "");
  return digitsOnly.length === 10;
};

const StudentAmbassadorForm = ({ onSuccess }) => {
  // Accept onSuccess as a prop
  const [step, setStep] = useState(1);
  const [subType, setSubType] = useState(null);
  const [direction, setDirection] = useState(1);
  const [receiveUpdates, setReceiveUpdates] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Form fields
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [institution, setInstitution] = useState("");
  const [involvement, setInvolvement] = useState("");
  const [phone, setPhone] = useState("");
  const [graduatingYear, setGraduatingYear] = useState("");
  const [major, setMajor] = useState("");
  const [experience, setExperience] = useState("");

  const SubTypeButton = ({ title, description, onClick, isSelected }) => (
    <button
      type="button"
      onClick={onClick}
      className={`
        border-2 rounded-md p-3 sm:p-4 w-full text-left transition-all duration-150 ease-in-out 
        min-h-[130px] sm:min-h-[145px] flex flex-col justify-center
        ${
          isSelected
            ? "border-[#5379F6] bg-[#5379F6]/10 shadow-lg"
            : "border-gray-300 bg-white hover:border-[#5379F6] hover:shadow-md"
        }
        focus:outline-none focus:ring-1 focus:ring-[#5379F6]
      `}
    >
      <h4 className="font-medium text-black text-base sm:text-xl mb-2">
        {title}
      </h4>
      <p className="text-sm sm:text-base text-[#5C5C5C]">{description}</p>
    </button>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess("");

    // Validate required fields including agreement
    if (
      !subType ||
      !name ||
      !email ||
      !institution ||
      !major ||
      !graduatingYear ||
      !involvement ||
      !experience ||
      !agreementAccepted // Add agreement validation
    ) {
      setError(
        !agreementAccepted
          ? "Please accept the agreement to continue"
          : "Please fill in all required fields"
      );
      setLoading(false);
      return;
    }

    // Validate name
    if (!validateName(name)) {
      setError("Name should only contain letters");
      setLoading(false);
      return;
    }

    // Validate email
    if (!validateEmail(email)) {
      setError("Please use a valid email address. For now, a personal email (Gmail) is recommended.");
      setLoading(false);
      return;
    }

    // Validate phone number
    if (!validatePhoneNumber(phone)) {
      setError("Please enter a valid 10-digit phone number.");
      setLoading(false);
      return;
    }

    // Validate major (text only)
    if (!validateText(major)) {
      setError("Major should only contain letters");
      setLoading(false);
      return;
    }

    try {
      const submissionData = {
        name,
        school_email: email, // Use school_email for consistency
        institution,
        school_type: subType,
        phone: phone || null,
        graduating_year: graduatingYear,
        major,
        club_involvement: involvement,
        leadership_experience: experience,
        agreement_accepted: agreementAccepted,
      };

      const submissionType = "ambassador";

      // Call the verification edge function
      const { error: functionError } = await supabase.functions.invoke(
        "send-waitlist-verification",
        {
          body: { submissionData, submissionType },
        }
      );

      if (functionError) {
        if (functionError.message.includes("already been submitted")) {
          throw new Error("This email address has already been used to apply.");
        }
        throw functionError;
      }

      // Call the parent's success handler instead of setting local state
      onSuccess();
    } catch (error) {
      console.error("Error:", error);
      setError(
        error.message ||
          "An error occurred while submitting the form. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  // Validation functions
  const validateName = (name) => {
    return /^[a-zA-Z\s-']+$/.test(name); // Only letters, spaces, hyphens, and apostrophes
  };

  const validateEmail = (email) => {
    return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.(edu|org|com)$/.test(email);
  };

  const validateText = (text) => {
    return /^[a-zA-Z\s-']+$/.test(text); // Only letters, spaces, hyphens, and apostrophes
  };

  const resetForm = () => {
    setStep(1);
    setSubType(null);
    setDirection(1);
    setReceiveUpdates(false);
    setAgreementAccepted(false);
    setError("");
    setSuccess("");
    setLoading(false);

    // Reset all form fields
    setName("");
    setEmail("");
    setInstitution("");
    setInvolvement("");
    setPhone("");
    setGraduatingYear("");
    setMajor("");
    setExperience("");
  };

  return (
    <div className="bg-white p-8 md:p-10 rounded-lg shadow-[0_0_20px_rgba(0,0,0,0.1)] border-2 border-gray-200 w-full max-w-3xl font-inter">
      <div className="text-center mb-4">
        <h2 className="text-4xl font-bold text-black">
          Become a Student Ambassador
        </h2>
        <p className="text-lg text-[#5C5C5C] mt-2">
          Help us shape the future of campus life
        </p>
      </div>

      <div className="w-full max-w-2xl mx-auto">
        {/* Step 1: Only show type selection */}
        {step === 1 && (
          <div className="space-y-6">
            <div>
              <label className="block text-lg font-medium text-[#4A4A4A] mb-3">
                Are you a high school or college/university student?
              </label>
              <div className="flex space-x-3">
                <SubTypeButton
                  title="High School"
                  description="Currently enrolled in grades 9-12"
                  onClick={() => {
                    setSubType("highschool");
                    setInstitution(""); // Reset institution when type changes
                    setStep(2);
                  }}
                  isSelected={subType === "highschool"}
                />
                <SubTypeButton
                  title="College/University"
                  description="Currently enrolled in higher education"
                  onClick={() => {
                    setSubType("college"); // Changed from "higherEducation"
                    setInstitution(""); // Reset institution when type changes
                    setStep(2);
                  }}
                  isSelected={subType === "college"}
                />
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Show form with submit button */}
        {step === 2 && (
          <form onSubmit={handleSubmit} className="space-y-6">
            <motion.div layout className="relative overflow-hidden">
              <AnimatePresence initial={false} mode="wait">
                <motion.div className="space-y-6">
                  <div className="space-y-4">
                    {/* Institution Type Selection */}
                    <div>
                      <label className="block text-lg font-medium text-[#4A4A4A] mb-3">
                        Are you a high school or college student?
                      </label>
                      <div className="flex space-x-3">
                        <SubTypeButton
                          title="High School Student"
                          description="Currently enrolled in grades 9-12"
                          onClick={() => {
                            setSubType("highschool");
                            setInstitution("");
                          }}
                          isSelected={subType === "highschool"}
                        />
                        <SubTypeButton
                          title="College/University Student"
                          description="Currently enrolled in higher education"
                          onClick={() => {
                            setSubType("college");
                            setInstitution("");
                          }}
                          isSelected={subType === "college"}
                        />
                      </div>
                    </div>

                    {/* Form Fields */}
                    <AnimatePresence>
                      {subType && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          className="space-y-4 pt-4 border-t border-gray-200 mt-4"
                        >
                          {/* Full Name */}
                          <div>
                            <label
                              htmlFor="name"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Full Name
                            </label>
                            <input
                              id="name"
                              type="text"
                              required
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-lg focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={name}
                              onChange={(e) => setName(e.target.value)}
                            />
                          </div>

                          {/* Institution Dropdown */}
                          <div>
                            <label
                              htmlFor="institution"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Institution
                            </label>
                            <InstitutionSelector
                              schoolType={subType} // subType will now be either "highschool" or "college"
                              required
                              onSelect={(institution) => {
                                setInstitution(institution.name);
                              }}
                            />
                          </div>

                          {/* Email Input */}
                          <div>
                            <label
                              htmlFor="email"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Email
                            </label>
                            <p className="text-sm text-[#4A4A4A] mt-1 mb-2">
                            Note: Some .edu and .org emails aren’t working for now. Please use a personal email (like Gmail) to get verification link.
                            </p>
                            <input
                              id="email"
                              type="email"
                              required
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-lg focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                            />
                          </div>

                          {/* Phone Input (Optional) */}
                          <div>
                            <label
                              htmlFor="phone"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Phone Number (Optional)
                            </label>
                            <input
                              id="phone"
                              type="tel"
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-lg focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={phone}
                              onChange={(e) => {
                                const formatted = formatPhoneNumber(
                                  e.target.value
                                );
                                setPhone(formatted);
                              }}
                            />
                          </div>

                          {/* Graduating Year and Major in same row */}
                          <div className="flex space-x-4">
                            {/* Graduating Year */}
                            <div className="w-1/2">
                              <label
                                htmlFor="graduatingYear"
                                className="block text-lg font-medium text-[#4A4A4A]"
                              >
                                Graduating Year
                              </label>
                              <select
                                id="graduatingYear"
                                required
                                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-lg focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                                value={graduatingYear}
                                onChange={(e) =>
                                  setGraduatingYear(e.target.value)
                                }
                                style={{ height: "44px" }}
                              >
                                <option value="">Select year</option>
                                <option value="2025">2025</option>
                                <option value="2026">2026</option>
                                <option value="2027">2027</option>
                                <option value="2028">2028</option>
                                <option value="2029">2029</option>
                                <option value="2030">2030</option>
                              </select>
                            </div>

                            {/* Major */}
                            <div className="w-1/2">
                              <label
                                htmlFor="major"
                                className="block text-lg font-medium text-[#4A4A4A]"
                              >
                                Major
                              </label>
                              <input
                                id="major"
                                type="text"
                                required
                                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-lg focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                                value={major}
                                onChange={(e) => setMajor(e.target.value)}
                              />
                            </div>
                          </div>

                          {/* Club Involvement */}
                          <div>
                            <label
                              htmlFor="involvement"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              What clubs or activities are you involved in?
                            </label>
                            <textarea
                              id="involvement"
                              required
                              rows={3}
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-lg focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={involvement}
                              onChange={(e) => setInvolvement(e.target.value)}
                              placeholder="List your club memberships and campus activities..."
                            />
                          </div>

                          {/* Leadership Experience */}
                          <div>
                            <label
                              htmlFor="experience"
                              className="block text-lg font-medium text-[#4A4A4A]"
                            >
                              Do you have experience leading events, clubs, or
                              communities?
                            </label>
                            <textarea
                              id="experience"
                              required
                              rows={3}
                              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-lg focus:outline-none focus:ring-[#5379F6] focus:border-[#5379F6]"
                              value={experience}
                              onChange={(e) => setExperience(e.target.value)}
                              placeholder="Tell us about your leadership experience..."
                            />
                          </div>

                          <hr />
                          <div className="pt-2 pb-4 flex items-center space-x-3">
                            <input
                              type="checkbox"
                              id="agreements"
                              className="w-6 h-6 accent-[#5379F6] border-[#5379F6] border-2 rounded focus:ring-[#5379F6]"
                              checked={agreementAccepted}
                              onChange={(e) =>
                                setAgreementAccepted(e.target.checked)
                              }
                              required
                            />
                            <label
                              htmlFor="agreements"
                              className="ml-2 text-lg font-medium text-[#4A4A4A]"
                            >
                              I agree to represent Scholarty in a positive and
                              professional manner if selected.
                            </label>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              </AnimatePresence>
            </motion.div>

            {/* Submit button and status messages */}
            <div className="w-full space-y-4 mt-8">
              <div className="w-full px-1">
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full flex justify-center py-4 px-6 border border-transparent rounded-md shadow-sm text-xl font-medium text-white bg-[#5379F6] hover:bg-[#4A6ACB] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#5379F6] disabled:opacity-50"
                >
                  {loading ? "Submitting..." : "Apply to be an Ambassador"}
                </button>
              </div>

              <div className="w-full px-1">
                {error && (
                  <div className="text-red-500 text-center p-2">{error}</div>
                )}
                {success && (
                  <div className="text-green-500 text-center p-2">
                    {success}
                  </div>
                )}
                {loading && (
                  <div className="text-blue-500 text-center p-2">
                    Submitting...
                  </div>
                )}
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default StudentAmbassadorForm;
